/* eslint-disable react-hooks/exhaustive-deps */
import {
  Box,
  Button,
  ButtonGroup,
  Divider,
  Flex,
  Heading,
  HStack,
  IconButton,
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  Menu,
  MenuButton,
  MenuItemOption,
  MenuList,
  MenuOptionGroup,
  Spacer,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  Tooltip,
  useToast,
} from '@chakra-ui/react';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import CQContainer from '../../components/layout/CQContainer';
import AppColors from '../../constants/AppColors';
import AuditProvider, { AuditContext } from '../../providers/AuditProvider';
import { withMultiProviders } from '../../hoc/withProvider';
import { useNavigate } from 'react-router-dom';
import ROUTES from '../../constants/Routes';
import { FONT_FAMILY } from '../../constants/Theme';
import { SearchIcon, ChevronDownIcon, ChevronUpIcon, CloseIcon, RepeatIcon } from '@chakra-ui/icons';
import { ToastLoading } from '../../components/Loader';
import { useTabStorage, usePaginationSessionStorage, useBoolean } from '@laxmimanogna/code-quick-components';
import { CustomTab } from '../../components/CustomTab';
import ChartUploadProvider from '../../providers/ChartUploadProvider';
import NewAuditTable from './components/tables/NewAuditTable';
import AuditSheetProvider, { AuditSheetContext } from '../../providers/AuditSheetProvider';
import { ChartFilterInfo, FILTER_OPTIONS, FILTER_OPTIONS_TO_CHART_STATUS_MAP } from '../../constants/constants';
import { DASHBOARD_PAGE_NAME, DASHBOARD_TABLE_NAME } from './constants';

const AssignedAuditTabs = [
  
  {
    title: 'All Charts',
    header: 'All Charts',
    component: NewAuditTable,
  },
];


const AuditUploadContainerV2 = () => {
  const toast = useToast();
  const navigate = useNavigate();
  const tabStorage = useTabStorage();
  const auditContext = useContext(AuditContext);
  const [resetFilterTrigger, rftState] = useBoolean(null);
  const [tableToggleData, setTableToggleData] = useState({
    expand:0,
    collapse:0
  })

  const DEFAULT_FILTER_NAME = useMemo(() => "All", []);
  const DEFAULT_FILTER_POS = useMemo(() => FILTER_OPTIONS.indexOf(DEFAULT_FILTER_NAME), [DEFAULT_FILTER_NAME])
  const { 
      allCharts, 
      isAllChartsLoading, 
      getAllCharts, 
      filterParams,
      updateChartsV2,
      updateFilterParams,
      handleApplyFilter,
  } = auditContext;

  const {
    getPaginationSession,
    setPaginationSession
  } = usePaginationSessionStorage();

  const auditSheetContext = useContext(AuditSheetContext)

  const [selectedTab, setSelectedTab] = useState(0);
  
  const onOpenNotes = (obj, batchId, tabType = undefined) => {
    if (obj && Object.keys(obj).length) {
      if (obj.all) {
        delete auditSheetContext.commentFilters.sheet_name
        auditSheetContext.setCommentFilters({
          ...auditSheetContext.commentFilters,
          page: 1,
          tabType: tabType
        })
      } else if (obj.current) {
        auditSheetContext.setCommentFilters({
          ...auditSheetContext.commentFilters,
          sheet_name: "Sheet1",
          page: 1,
          tabType: tabType
        })
      }
    } else {
      auditSheetContext.setCommentFilters({
        ...auditSheetContext.commentFilters,
        batchId,
        sheet_name: "Sheet1",
        reverse: true,
        page: 1,
        tabType: tabType
      })
    }
  }

  const dropdownOptions = useMemo(
    () => FILTER_OPTIONS.map(val => ({
      value: val,
      label: val,
      // ChartFilterInfo names don't always match exactly (e.g. "Rebuttal" vs "Rebuttals")
      description: ChartFilterInfo.find(
        info => info.name === val || `${info.name}s` === val
      )?.definition,
    })),
    []
  );

  const [selectedFilter, setSelectedFilter] = useState(() => ({
    value: FILTER_OPTIONS[tabStorage.getTab() ?? DEFAULT_FILTER_POS],
    label: FILTER_OPTIONS[tabStorage.getTab() ?? DEFAULT_FILTER_POS]
  }));

  useEffect(() => {
    if (!selectedFilter.value) {
      return;
    }
    tabStorage.setTab(FILTER_OPTIONS.indexOf(selectedFilter.value))
  }, [selectedFilter]);

  const { loadSpecialtyAuditors } = auditContext;

  useEffect(() => {
    auditContext.getAllUsers();

    loadSpecialtyAuditors();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    showLoaderToast(auditContext.isUpdating);
  }, [auditContext.isUpdating]);

  useEffect(() => {
    showLoaderToast(auditContext.isAssigningChartToSelf);
  }, [auditContext.isAssigningChartToSelf]);

  function showLoaderToast(isLoading) {
    if (isLoading) {
      toast({
        render: ToastLoading,
        duration: null,
        id: 'loading-toast',
        position: 'top-right',
      });
    } else {
      toast.close('loading-toast');
    }
  }

  const onSearchAction = () => {
    handleApplyFilter({
      ...filterParams,
      triggerSearch: !filterParams.triggerSearch,
    });
  };

  const onFilterChange = e => {
     handleApplyFilter({
      ...filterParams,
      searchText: e.target.value
    })
  };

  const onClearSearch = () => {
    handleApplyFilter({
      ...filterParams,
      searchText: '',
      triggerSearch: !filterParams.triggerSearch,
    });
  };

  const onStatusChange = option => {
    setSelectedFilter(option);
    handleApplyFilter({
      ...filterParams,
      triggerSearch: !filterParams.triggerSearch,
      chartStatusType: option.value
    });
  };

  const hasActiveFilters =
    selectedFilter.value !== DEFAULT_FILTER_NAME ||
    !!filterParams?.searchText ||
    !!filterParams?.ordering;

  const onResetFilters = () => {
    setSelectedFilter(dropdownOptions[DEFAULT_FILTER_POS]);
    setPaginationSession(
      DASHBOARD_PAGE_NAME,
      DASHBOARD_TABLE_NAME,
      {
        ...getPaginationSession(DASHBOARD_PAGE_NAME, DASHBOARD_TABLE_NAME),
        page: 1,
        size: 10
      }
    );
    handleApplyFilter({
      searchText: "",
      triggerSearch: !filterParams.triggerSearch,
      ordering: "",
      chartStatusType: DEFAULT_FILTER_NAME,
    });
    rftState.toggle();
  };

  return (
    <CQContainer>
      <HStack mb={5}>
        <Heading as="h5" fontSize="24px">
          {AssignedAuditTabs[0].header}
        </Heading>
        <Spacer />
        <Button
          mr={1}
          color={AppColors.secondary}
          // style={{ paddingInline: '20px', outlineColor: AppColors.secondary }}
          _hover={{bg:AppColors.secondary,color:AppColors.white}}
          width={'203px'}
          height={'50px'}
          border={'1px solid'}
          borderColor={AppColors.secondary}
          borderRadius="3xl"
          bgColor={'transparent'}
          fontSize={'16px'}
          onClick={() => {
            navigate(ROUTES.OFFLINE_UPLOAD_CHART);
          }}
        >
          Offline Chart Upload
        </Button>
      </HStack>
      <Box bg="white" p={4} w="100%" style={{ borderRadius: '12px' }} marginLeft={0}>
        <Tabs
          fontFamily={FONT_FAMILY.baiJamurjee}
          onChange={setSelectedTab}
          index={selectedTab}
        >
          <Flex
            align={{ base: 'stretch', lg: 'center' }}
            direction={{ base: 'column', lg: 'row' }}
            gap={3}
            pb={4}
            mb={2}
            borderBottom="1px solid"
            borderColor="gray.200"
          >
            {/* Status filter */}
            <HStack spacing={3}>
              <Menu placement="bottom-start" isLazy>
                <MenuButton
                  as={Button}
                  aria-label="Filter by chart status"
                  size="md"
                  variant="outline"
                  minW="220px"
                  textAlign="left"
                  fontWeight={500}
                  color="gray.700"
                  borderColor="gray.300"
                  rightIcon={<ChevronDownIcon />}
                  isLoading={isAllChartsLoading}
                  isDisabled={isAllChartsLoading}
                >
                  {selectedFilter.label}
                </MenuButton>
                <MenuList zIndex={10} maxW="360px" py={1}>
                  <MenuOptionGroup
                    type="radio"
                    value={selectedFilter.value}
                    onChange={value => {
                      const option = dropdownOptions.find(o => o.value === value);
                      if (option && option.value !== selectedFilter.value) {
                        onStatusChange(option);
                      }
                    }}
                  >
                    {dropdownOptions.map(option => (
                      <MenuItemOption key={option.value} value={option.value} py={2}>
                        <Text fontSize="sm" fontWeight={600} color="gray.800">
                          {option.label}
                        </Text>
                        {option.description && (
                          <Text fontSize="xs" color="gray.500" mt={0.5}>
                            {option.description}
                          </Text>
                        )}
                      </MenuItemOption>
                    ))}
                  </MenuOptionGroup>
                </MenuList>
              </Menu>
            </HStack>

            <Spacer display={{ base: 'none', lg: 'block' }} />

            <HStack spacing={2}>
              {/* Row expand / collapse */}
              <ButtonGroup size="md" isAttached variant="outline">
                <Tooltip label="Expand all rows" hasArrow openDelay={300}>
                  <IconButton
                    aria-label="Expand all rows"
                    icon={<ChevronDownIcon boxSize={5} />}
                    borderColor="gray.300"
                    color="gray.600"
                    onClick={() => setTableToggleData({
                      ...tableToggleData,
                      expand: tableToggleData.expand + 1})
                    }
                  />
                </Tooltip>
                <Tooltip label="Collapse all rows" hasArrow openDelay={300}>
                  <IconButton
                    aria-label="Collapse all rows"
                    icon={<ChevronUpIcon boxSize={5} />}
                    borderColor="gray.300"
                    color="gray.600"
                    onClick={() => setTableToggleData({
                      ...tableToggleData,
                      collapse: tableToggleData.collapse + 1})
                    }
                  />
                </Tooltip>
              </ButtonGroup>

              <Divider orientation="vertical" h="24px" borderColor="gray.300" />

              {/* Search */}
              <InputGroup size="md" w={{ base: '100%', lg: '320px' }}>
                <InputLeftElement pointerEvents="none">
                  <SearchIcon color="gray.400" />
                </InputLeftElement>
                <Input
                  value={filterParams?.searchText ?? ''}
                  onChange={onFilterChange}
                  onKeyDown={e => {
                    if (e.key === 'Enter') {
                      onSearchAction();
                    }
                  }}
                  placeholder="Search charts and press Enter"
                  borderRadius="md"
                  borderColor="gray.300"
                  focusBorderColor={AppColors.secondary}
                  isDisabled={isAllChartsLoading}
                />
                {filterParams?.searchText && (
                  <InputRightElement>
                    <IconButton
                      size="xs"
                      variant="ghost"
                      aria-label="Clear search"
                      icon={<CloseIcon boxSize={2.5} />}
                      onClick={onClearSearch}
                      isDisabled={isAllChartsLoading}
                    />
                  </InputRightElement>
                )}
              </InputGroup>

              {/* Reset */}
              <Tooltip label="Reset status, search and sorting" hasArrow openDelay={300}>
                <Button
                  size="md"
                  variant="outline"
                  leftIcon={<RepeatIcon />}
                  fontWeight={500}
                  color="gray.700"
                  borderColor="gray.300"
                  flexShrink={0}
                  isDisabled={isAllChartsLoading || !hasActiveFilters}
                  onClick={onResetFilters}
                >
                  Reset
                </Button>
              </Tooltip>
            </HStack>
          </Flex>

          <TabPanels>
            {AssignedAuditTabs.map((aa, index) => {
              return (
                <TabPanel p={0} key={`assigned-audit-panel-${index}`}>
                  <aa.component 
                  key={`my-charts-panel-${index}`} 
                  onOpenNotes={onOpenNotes}
                  PAGE_NAME={DASHBOARD_PAGE_NAME}
                  TABLE_NAME={DASHBOARD_TABLE_NAME}
                  filterResetTrigger={resetFilterTrigger}
                  tableToggleData={tableToggleData}
                  />
                </TabPanel>
              );
            })}
          </TabPanels>
        </Tabs>
      </Box>
    </CQContainer>
  );
};

export default withMultiProviders(
  [AuditProvider, ChartUploadProvider, AuditSheetProvider],
  AuditUploadContainerV2
);
